import { currencyFormatter } from './currency';

const RELATION_TYPES = {
  employee: 'Employee',
  spouse: 'Spouse',
  child: 'Child',
  '': 'Invalid Relationship',
};

const UNI_TOBACCO_KEYS = {
  employee: 'rate_ee_uni_tobacco',
  spouse: 'rate_sp_uni_tobacco',
  child: 'rate_ch_uni_tobacco',
};

const TOBACCO_KEYS = {
  employee: 'rate_ee_tobacco',
  spouse: 'rate_sp_tobacco',
  child: 'rate_ee_tobacco',
};

const NON_TOBACCO_KEYS = {
  employee: 'rate_ee_non_tobacco',
  spouse: 'rate_sp_non_tobacco',
  child: 'rate_ee_non_tobacco',
};

export const normalizeRateValue = (value, digits = 3) => {
  return parseFloat(currencyFormatter(value, digits)?.replaceAll('$', '').replace(/,/g, ''));
};

const calculateRelationship = (member, offer, isTobacco) => {
  let payload = {
    ...member,
    type: RELATION_TYPES[member?.relationship || ''],
  };

  const death_benefit = offer?.death_benefit || 0;
  const member_life_benefit = member?.life_benefit_amount || 0;
  const member_salary = member?.salary_amount || 0;

  const rate_per_unit = offer?.rate_per_unit;

  const tobacco_value = offer?.[TOBACCO_KEYS[member?.relationship]] || 0;
  const non_tobacco_value = offer?.[NON_TOBACCO_KEYS[member?.relationship]] || 0;
  const uni_tobacco_value = offer?.[UNI_TOBACCO_KEYS[member?.relationship]] || 0;

  const is_uni_tobacco = offer?.is_uni_tobacco;

  if (is_uni_tobacco) {
    if (!death_benefit?.includes('salary')) {
      if (offer?.contribution_type === 'employer') {
        const rateValue = (death_benefit / rate_per_unit) * uni_tobacco_value;

        payload.tobacco = normalizeRateValue(rateValue);
        payload.tobaccoEmployer = normalizeRateValue(rateValue);
      }

      if (offer?.contribution_type === 'voluntary') {
        if (!offer?.is_current_rate) {
          const rateValue = (death_benefit / rate_per_unit) * uni_tobacco_value;

          payload.tobacco = normalizeRateValue(rateValue);
        }

        if (offer?.is_current_rate) {
          const rateValue = (member_life_benefit / rate_per_unit) * uni_tobacco_value;

          payload.tobacco = normalizeRateValue(rateValue);
        }
      }
    }

    if (death_benefit?.includes('salary')) {
      if (death_benefit === '1xsalary') {
        const rateValue = (member_salary / rate_per_unit) * uni_tobacco_value;

        payload.tobacco = normalizeRateValue(rateValue);
      }

      if (death_benefit === '2xsalary') {
        const rateValue = ((member_salary * 2) / rate_per_unit) * uni_tobacco_value;

        payload.tobacco = normalizeRateValue(rateValue);
      }
    }
  }

  if (!is_uni_tobacco) {
    if (!death_benefit?.includes('salary')) {
      if (offer?.contribution_type === 'employer') {
        const tobaccoValue = (death_benefit / rate_per_unit) * tobacco_value;
        const nonTobaccoValue = (death_benefit / rate_per_unit) * non_tobacco_value;

        if (isTobacco) {
          payload.tobacco = normalizeRateValue(tobaccoValue);
          payload.tobaccoEmployer = normalizeRateValue(tobaccoValue);
          payload.tobaccoType = 'Tobacco';
        }

        if (!isTobacco) {
          payload.nonTobacco = normalizeRateValue(nonTobaccoValue);
          payload.nonTobaccoEmployer = normalizeRateValue(nonTobaccoValue);
          payload.tobaccoType = 'Non-Tobacco';
        }
      }

      if (offer?.contribution_type === 'voluntary') {
        const tobaccoValue = (member_life_benefit / rate_per_unit) * tobacco_value;
        const nonTobaccoValue = (member_life_benefit / rate_per_unit) * non_tobacco_value;

        if (isTobacco) {
          payload.tobacco = normalizeRateValue(tobaccoValue);
          payload.tobaccoType = 'Tobacco';
        }

        if (!isTobacco) {
          payload.nonTobacco = normalizeRateValue(nonTobaccoValue);
          payload.tobaccoType = 'Non-Tobacco';
        }
      }
    }

    if (death_benefit?.includes('salary')) {
      if (death_benefit === '1xsalary') {
        const tobaccoValue = (member_salary / rate_per_unit) * tobacco_value;
        const nonTobaccoValue = (member_salary / rate_per_unit) * non_tobacco_value;

        if (isTobacco) {
          payload.tobacco = normalizeRateValue(tobaccoValue);
          payload.tobaccoType = 'Tobacco';
        }

        if (!isTobacco) {
          payload.nonTobacco = normalizeRateValue(nonTobaccoValue);
          payload.tobaccoType = 'Non-Tobacco';
        }
      }

      if (death_benefit === '2xsalary') {
        const tobaccoValue = ((member_salary * 2) / rate_per_unit) * tobacco_value || 0;
        const nonTobaccoValue = ((member_salary * 2) / rate_per_unit) * non_tobacco_value || 0;

        if (isTobacco) {
          payload.tobacco = normalizeRateValue(tobaccoValue);
          payload.tobaccoType = 'Tobacco';
        }

        if (isTobacco) {
          payload.nonTobacco = normalizeRateValue(nonTobaccoValue);
          payload.tobaccoType = 'Non-Tobacco';
        }
      }
    }
  }

  return payload;
};

const calculateLifeHousehold = (householdFamilies, selectedOffer) => {
  let payload = {};

  for (const family_id in householdFamilies) {
    const household = householdFamilies[family_id];

    const isTobaccoUsers = household?.find((item) => item?.is_tobacco_user);

    const updatedHousehold = [];

    for (const member of household) {
      const payload = calculateRelationship(member, selectedOffer, isTobaccoUsers);

      updatedHousehold.push(payload);
    }

    payload = {
      ...payload,
      [family_id]: updatedHousehold,
    };
  }
  return payload;
};

export const calculateLifeRates = (accountMembers, selectedOffer) => {
  let household = {};

  const householdFamilies = accountMembers.reduce((previousValue, currentValue) => {
    if (typeof previousValue[currentValue.family_id] === 'undefined') {
      previousValue[currentValue.family_id] = [];
    }

    previousValue[currentValue.family_id].push(currentValue);

    return previousValue;
  }, {});

  household = calculateLifeHousehold(householdFamilies, selectedOffer);

  household.totalTobacco = 0;
  household.totalEmployerTobacco = 0;

  const excludes = ['totalTobacco', 'totalEmployerTobacco'];

  for (const family_id in household) {
    if (!excludes.includes(family_id)) {
      for (const member of household[family_id]) {
        household.totalTobacco += member?.tobacco || 0;
        household.totalTobacco += member?.nonTobacco || 0;

        household.totalEmployerTobacco += member?.tobaccoEmployer || 0;
        household.totalEmployerTobacco += member?.tobaccoNonEmployer || 0;
      }
    }
  }

  household.totalTobacco = normalizeRateValue(household.totalTobacco);
  household.totalEmployerTobacco = normalizeRateValue(household.totalEmployerTobacco);

  return household;
};

export const calculateUniLifeRates = (accountMembers, selectedOffer) => {
  let household = {};

  const householdFamilies = accountMembers.reduce((previousValue, currentValue) => {
    if (typeof previousValue[currentValue.family_id] === 'undefined') {
      previousValue[currentValue.family_id] = [];
    }

    previousValue[currentValue.family_id].push(currentValue);

    return previousValue;
  }, {});

  household = calculateLifeHousehold(householdFamilies, selectedOffer);

  household.totalTobacco = 0;
  household.totalEmployerTobacco = 0;

  const excludes = ['totalTobacco', 'totalEmployerTobacco'];

  for (const family_id in household) {
    if (!excludes.includes(family_id)) {
      for (const member of household[family_id]) {
        household.totalTobacco += member?.tobacco || 0;
        household.totalEmployerTobacco += member?.tobaccoEmployer || 0;
      }
    }
  }

  household.totalTobacco = normalizeRateValue(household.totalTobacco);
  household.totalEmployerTobacco = normalizeRateValue(household.totalEmployerTobacco);

  return household;
};
